import { NotificationConfigurationModal } from "@/Administration/Components/Services/ServiceComboTabView";
import { AdministrationHelper } from "@/Administration/Helpers/AdministrationHelper";
import { AdminServiceGridSortColumn } from "@/Administration/Models/Enums/AdminServiceGridSortColumn";
import { ServiceComboDisplaySettingsModel } from "@/Administration/Models/Settings/ServiceComboDisplaySettingsModel";
import { ServiceComboEditMode } from "@/Administration/Models/UI/Enums/Administration";
import { IServiceComboGridModel } from "@/Administration/Models/UI/ServiceCombo/IServiceComboGridModel";
import { AdministrationStore } from "@/Administration/Stores/AdministrationStore";
import { IComboOrServiceItem } from "@/Common/Models/Services/BaseServiceDefinitionModel";
import { ServiceComboItemType } from "@/Common/Models/Services/Enums/ServiceComboItemType";
import { ServiceComboDefinitionModel } from "@/Common/Models/Services/ServiceComboDefinitionModel";
import { ServiceDefinitionModel } from "@/Common/Models/Services/ServiceDefinitionModel";
import { ServiceOrComboTableItem } from "@/Common/Models/Services/ServiceOrComboTableItem";
import { SortDirection } from "@/Common/Models/UI/Enums";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";

class ServiceComboHandler extends BaseOperationHandler {
    constructor() {
        super();
        this.RegisterOperation(LocalOperations.AdministrationServiceCombo.ServiceDefinitionEdit_Open, this.openServiceDefinitionEditPopup);
        this.RegisterOperation(LocalOperations.AdministrationServiceCombo.ServiceDefinitionEdit_Close, this.closeServiceDefinitionEditPopup);
        this.RegisterOperation(LocalOperations.AdministrationServiceCombo.ServiceDefinitionPortalDetailsEdit_Open, this.openPortalDetailsEditPopup);
        this.RegisterOperation(LocalOperations.AdministrationServiceCombo.ServiceDefinitionPortalDetailsEdit_Close, this.closePortalDetailsEditPopup);
        this.RegisterOperation(LocalOperations.AdministrationServiceCombo.CreateService_Click, this.openCreateServicePopup);
        this.RegisterOperation(LocalOperations.AdministrationServiceCombo.CreateCombo_Click, this.openCreateComboPopup);
        this.RegisterOperation(LocalOperations.AdministrationServiceCombo.SaveService_Click, this.saveService, false);
        this.RegisterOperation(LocalOperations.AdministrationServiceCombo.SaveCombo_Click, this.saveCombo, false);
        this.RegisterOperation(LocalOperations.AdministrationServiceCombo.RefreshGrid, this.refreshServices);
        this.RegisterOperation(LocalOperations.AdministrationServiceCombo.SearchSettings_Open, this.openSearchSettings);
        this.RegisterOperation(LocalOperations.AdministrationServiceCombo.SearchSettings_Close, this.closeSearchSettings);
        this.RegisterOperation(LocalOperations.AdministrationServiceCombo.SearchSettings_SearchTextChanged, this.searchSettingsNameFilterChanged);
        this.RegisterOperation(LocalOperations.AdministrationServiceCombo.SaveDisplaySettings, this.saveDisplaySettings);
        this.RegisterOperation(LocalOperations.AdministrationServiceCombo.ExportService_Open, this.openExportServices);
        this.RegisterOperation(LocalOperations.AdministrationServiceCombo.ExportService_Close, this.closeExportServices);
        this.RegisterOperation(LocalOperations.AdministrationServiceCombo.NotificationConfiguration_Open, this.openNotificationConfiguration);
        this.RegisterOperation(LocalOperations.AdministrationServiceCombo.NotificationConfiguration_Close, this.closeNotificationConfiguration);
        this.RegisterOperation(LocalOperations.Misc.Table_ColumnSortClick, this.columnSort);
        this.RegisterOperation(LocalOperations.Misc.MyleApp_Container_Click, this.onContainerClick);
    }

    private refreshServices(request: LocalEvent, store: AdministrationStore): void {
        let doWeReload = request.Get<boolean>("reload");
        let model = request.Get<IServiceComboGridModel>("model");
        let closeSettings = request.Get<boolean>("closeSettings");

        if (doWeReload) {
            // Throtlled
            if (administrationStore.timeoutReferenceRefreshServices !== -1) {
                clearTimeout(administrationStore.timeoutReferenceRefreshServices);
                administrationStore.timeoutReferenceRefreshServices = -1;
            }

            administrationStore.timeoutReferenceRefreshServices = window.setTimeout(() => {
                cacheStore.actions.refreshServicesAndLookups();
            }, 700);
        }
        else {
            let filteredServices: ServiceOrComboTableItem<IComboOrServiceItem>[] = AdministrationHelper.FilterAndOrderServiceComboDefinitions(
                cacheStore.ServiceDefinitionCache.toArray(),
                cacheStore.ServiceDefinitionComboCache.toArray(),
                model != null ? model.get("EmployeesFiltered").toArray() : store.serviceComboGridModel.get("EmployeesFiltered").toArray(),
                model != null ? model.get("Sites").toArray() : store.serviceComboGridModel.get("Sites").toArray(),
                model != null ? model.get("Types").toArray() : store.serviceComboGridModel.get("Types").toArray(),
                model != null ? model.get("Specializations").toArray() : store.serviceComboGridModel.get("Specializations").toArray(),
                store.serviceComboGridModel.get("GridSortColumn"),
                store.serviceComboGridModel.get("GridSortDirection"),
                model != null ? model.get("Status_IsActiveChecked") : store.serviceComboGridModel.get("Status_IsActiveChecked"),
                model != null ? model.get("Status_IsInactiveChecked") : store.serviceComboGridModel.get("Status_IsInactiveChecked"),
                store.serviceComboGridModel.get("ServiceAndComboSearchText")
            );

            if (closeSettings)
                store.serviceComboGridModel = store.serviceComboGridModel.set("SearchSettingsOpen", false);

            if (model != null) {
                if (model.get("EmployeesFiltered").size == store.serviceComboGridModel.get("EmployeesFiltered").size)
                    store.serviceComboGridModel = store.serviceComboGridModel.set("EmployeesFiltered", model.get("EmployeesFiltered"));
                store.serviceComboGridModel = store.serviceComboGridModel.set("Sites", model.get("Sites"));
                store.serviceComboGridModel = store.serviceComboGridModel.set("Types", model.get("Types"));
                store.serviceComboGridModel = store.serviceComboGridModel.set("Specializations", model.get("Specializations"));
                store.serviceComboGridModel = store.serviceComboGridModel.set("Status_IsActiveChecked", model.get("Status_IsActiveChecked"));
                store.serviceComboGridModel = store.serviceComboGridModel.set("Status_IsInactiveChecked", model.get("Status_IsInactiveChecked"));
            }
            store.serviceComboGridModel = store.serviceComboGridModel.set("FilteredCombosAndServices", Immutable.List(filteredServices));
            store.usersTabModel = store.usersTabModel.set("UserServiceComboGridModel", store.serviceComboGridModel);
        }
    }

    private openServiceDefinitionEditPopup(request: LocalEvent, store: AdministrationStore): void {
        let rowObject: any = request.Get("rowData");

        if (rowObject instanceof ServiceDefinitionModel) {
            if (store.serviceComboGridModel.get("DisplayEditServiceComponent") != true) {
                let serviceDefinitionModel = (rowObject as ServiceDefinitionModel).Clone(true);
                store.serviceComboGridModel = store.serviceComboGridModel.set("DisplayEditServiceComponent", true);
                store.serviceComboGridModel = store.serviceComboGridModel.set("EditServiceComponentModel", serviceDefinitionModel);
                store.serviceComboGridModel = store.serviceComboGridModel.set("CurrentEditedType", ServiceComboItemType.Service);
                store.serviceComboGridModel = store.serviceComboGridModel.set("CurrentEditMode", ServiceComboEditMode.Edit);

                sessionStore.showGlobalOverlay(true);
            }
        }
        else if (rowObject instanceof ServiceComboDefinitionModel) {
            if (store.serviceComboGridModel.get("DisplayEditServiceComponent") != true) {
                let comboDefinitionModel = (rowObject as ServiceComboDefinitionModel).Clone(true);
                store.serviceComboGridModel = store.serviceComboGridModel.set("DisplayEditServiceComponent", true);
                store.serviceComboGridModel = store.serviceComboGridModel.set("EditServiceComponentModel", comboDefinitionModel);
                store.serviceComboGridModel = store.serviceComboGridModel.set("CurrentEditedType", ServiceComboItemType.Combo);
                store.serviceComboGridModel = store.serviceComboGridModel.set("CurrentEditMode", ServiceComboEditMode.Edit);

                sessionStore.showGlobalOverlay(true);
            }
        }
    }

    private closeServiceDefinitionEditPopup(request: LocalEvent, store: AdministrationStore): void {
        store.serviceComboGridModel = store.serviceComboGridModel.set("DisplayEditServiceComponent", false);
        store.serviceComboGridModel = store.serviceComboGridModel.set("EditServiceComponentModel", null);
        store.serviceComboGridModel = store.serviceComboGridModel.set("CurrentEditMode", ServiceComboEditMode.View);

        sessionStore.hideGlobalOverlay();
    }

    private openPortalDetailsEditPopup(request: LocalEvent, store: AdministrationStore): void {
        let rowObject: any = request.Get("rowData");

        if (rowObject instanceof ServiceDefinitionModel) {
            if (store.serviceComboGridModel.get("DisplayPortalDetailsComponent") != true) {
                let serviceDefinitionModel = (rowObject as ServiceDefinitionModel).Clone(true);
                store.serviceComboGridModel = store.serviceComboGridModel.set("DisplayPortalDetailsComponent", true);
                store.serviceComboGridModel = store.serviceComboGridModel.set("EditServiceComponentModel", serviceDefinitionModel);
                store.serviceComboGridModel = store.serviceComboGridModel.set("CurrentEditedType", ServiceComboItemType.Service);
                store.serviceComboGridModel = store.serviceComboGridModel.set("CurrentEditMode", ServiceComboEditMode.Edit);
                sessionStore.showGlobalOverlay(true);
            }
        }
        else if (rowObject instanceof ServiceComboDefinitionModel) {
            if (store.serviceComboGridModel.get("DisplayPortalDetailsComponent") != true) {
                let comboDefinitionModel = (rowObject as ServiceComboDefinitionModel).Clone(true);
                store.serviceComboGridModel = store.serviceComboGridModel.set("DisplayPortalDetailsComponent", true);
                store.serviceComboGridModel = store.serviceComboGridModel.set("EditServiceComponentModel", comboDefinitionModel);
                store.serviceComboGridModel = store.serviceComboGridModel.set("CurrentEditedType", ServiceComboItemType.Combo);
                store.serviceComboGridModel = store.serviceComboGridModel.set("CurrentEditMode", ServiceComboEditMode.Edit);
                sessionStore.showGlobalOverlay(true);
            }
        }

        store.serviceComboGridModel = store.serviceComboGridModel.set("DisplayPortalDetailsComponent", true);
    }

    private closePortalDetailsEditPopup(request: LocalEvent, store: AdministrationStore): void {
        store.serviceComboGridModel = store.serviceComboGridModel.set("DisplayPortalDetailsComponent", false);
        store.serviceComboGridModel = store.serviceComboGridModel.set("EditServiceComponentModel", null);
        store.serviceComboGridModel = store.serviceComboGridModel.set("CurrentEditMode", ServiceComboEditMode.View);
        sessionStore.hideGlobalOverlay();
    }

    private openCreateServicePopup(request: LocalEvent, store: AdministrationStore): void {
        let serviceDefinition = request.Get<ServiceDefinitionModel>("service");

        if (serviceDefinition instanceof ServiceDefinitionModel) {
            store.serviceComboGridModel = store.serviceComboGridModel.set("DisplayEditServiceComponent", true);
            store.serviceComboGridModel = store.serviceComboGridModel.set("EditServiceComponentModel", serviceDefinition);
            store.serviceComboGridModel = store.serviceComboGridModel.set("CurrentEditMode", ServiceComboEditMode.Create);
            store.serviceComboGridModel = store.serviceComboGridModel.set("CurrentEditedType", ServiceComboItemType.Service);
        }
    }

    private openCreateComboPopup(request: LocalEvent, store: AdministrationStore): void {
        let comboDefinition = request.Get<ServiceComboDefinitionModel>("combo");

        if (comboDefinition instanceof ServiceComboDefinitionModel) {
            store.serviceComboGridModel = store.serviceComboGridModel.set("DisplayEditServiceComponent", true);
            store.serviceComboGridModel = store.serviceComboGridModel.set("EditServiceComponentModel", comboDefinition);
            store.serviceComboGridModel = store.serviceComboGridModel.set("CurrentEditMode", ServiceComboEditMode.Create);
            store.serviceComboGridModel = store.serviceComboGridModel.set("CurrentEditedType", ServiceComboItemType.Combo);
        }
    }

    private saveService(request: LocalEvent, store: AdministrationStore): void {
        var serviceDefinitionModel = request.Get<ServiceDefinitionModel>("serviceDefinition");
        let isSavingOnlineInfo = request.Get<boolean>("isSavingOnlineInfo");
        store.serviceComboActions.SaveServiceDefinition(serviceDefinitionModel, !isSavingOnlineInfo);
    }

    private saveCombo(request: LocalEvent, store: AdministrationStore): void {
        let comboDefinition = request.Get<ServiceComboDefinitionModel>("comboDefinition");
        let isSavingOnlineInfo = request.Get<boolean>("isSavingOnlineInfo");
        store.serviceComboActions.SaveComboDefinition(comboDefinition, !isSavingOnlineInfo);
    }

    private saveDisplaySettings(request: LocalEvent, store: AdministrationStore): void {
        const model = request.Get<IServiceComboGridModel>("serviceComboGridModel");
        const serviceComboDisplaySettings = ServiceComboDisplaySettingsModel.FromModel(model);
        store.serviceComboActions.SaveDisplaySettings(
            serviceComboDisplaySettings,
            () => cacheStore.UserSettings.ServiceComboDisplay = serviceComboDisplaySettings
        );
    }

    private openSearchSettings(request: LocalEvent, store: AdministrationStore): void {
        let model = store.serviceComboGridModel;
        model = model.set("SearchSettingsOpen", !model.get("SearchSettingsOpen"));
        store.serviceComboGridModel = model;
    }

    private closeSearchSettings(request: LocalEvent, store: AdministrationStore): void {
        let model = store.serviceComboGridModel;
        model = model.set("SearchSettingsOpen", false);
        store.serviceComboGridModel = model;
    }

    private openExportServices(request: LocalEvent, store: AdministrationStore): void {
        let model = store.serviceComboGridModel;
        model = model.set("ExportServiceOpen", true);
        store.serviceComboGridModel = model;
    }

    private closeExportServices(request: LocalEvent, store: AdministrationStore): void {
        let model = store.serviceComboGridModel;
        model = model.set("ExportServiceOpen", false);
        store.serviceComboGridModel = model;
    }

    private openNotificationConfiguration(request: LocalEvent, store: AdministrationStore): void {
        const configuration = request.Get<NotificationConfigurationModal>("NotificationConfiguration");

        store.serviceComboGridModel = store.serviceComboGridModel.set("NotificationConfiguration", configuration);
    }

    private closeNotificationConfiguration(request: LocalEvent, store: AdministrationStore): void {
        store.serviceComboGridModel = store.serviceComboGridModel
            .set("NotificationConfiguration", { show: false, serviceOrCombo: null, notificationConfiguration: null, defaultNotificationConfiguration: null });
    }

    private columnSort(request: LocalEvent, store: AdministrationStore): void {
        let tableTarget = request.Get<MyleTableTarget>("target");
        if (tableTarget === MyleTableTarget.Myle_AdminService_ColumnSortClick) {
            let currentSortCol = store.serviceComboGridModel.get("GridSortColumn");
            let sortDir = store.serviceComboGridModel.get("GridSortDirection");
            let sortCol = request.Get<AdminServiceGridSortColumn>("sortColumn");

            if (currentSortCol != sortCol) {
                sortDir = SortDirection.ASC;
            }
            else {
                if (sortDir == SortDirection.ASC)
                    sortDir = SortDirection.DESC;
                else
                    sortDir = SortDirection.ASC;
            }

            store.serviceComboGridModel = store.serviceComboGridModel.set("GridSortColumn", sortCol);
            store.serviceComboGridModel = store.serviceComboGridModel.set("GridSortDirection", sortDir);

            // call filtering again so it applies new ordering
            request.AddItem("reload", false);
            this.refreshServices(request, store);
        }
    }

    private onContainerClick(request: LocalEvent, store: AdministrationStore): void {
        if (store.serviceComboGridModel.get("SearchSettingsOpen") == true && store.serviceComboGridModel.get("DisplayEditServiceComponent") == false) {
            this.closeSearchSettings(request, store);
        }
    }

    private searchSettingsNameFilterChanged(request: LocalEvent, store: AdministrationStore): void {
        let filter = request.Get<string>("filterText");
        store.serviceComboGridModel = store.serviceComboGridModel.set("ServiceAndComboSearchText", filter);
        this.refreshServices(request, store);
    }
}

export { ServiceComboHandler };
